import { useEffect, useState } from "react";
import { BellRing, Smartphone, Download, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const supported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone;
const b64 = (s) => { const p = "=".repeat((4 - (s.length % 4)) % 4); const r = atob((s + p).replace(/-/g, "+").replace(/_/g, "/")); return Uint8Array.from([...r].map((c) => c.charCodeAt(0))); };

function usePush() {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (supported()) navigator.serviceWorker.ready.then((r) => r.pushManager.getSubscription()).then((s) => setOn(!!s && Notification.permission === "granted")).catch(() => {}); }, []);
  const enable = async () => {
    setBusy(true);
    try {
      if ((await Notification.requestPermission()) !== "granted") throw new Error("Notifications are blocked. Allow them for this site in your browser settings.");
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/public-key");
      const sub = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64(data.key) }));
      await api.post("/push/subscribe", { subscription: sub.toJSON(), device: navigator.userAgent.slice(0, 120) });
      setOn(true); toast.success("Alerts are on for this device");
    } catch (e) { toast.error(e.message?.startsWith("Notifications") ? e.message : errMsg(e)); } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try { const s = await (await navigator.serviceWorker.ready).pushManager.getSubscription(); if (s) { await api.post("/push/unsubscribe", { endpoint: s.endpoint }); await s.unsubscribe(); } setOn(false); toast.success("Alerts turned off on this device"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return { on, busy, enable, disable };
}

function useInstall() {
  const [ready, setReady] = useState(!!window.__mhhInstall);
  useEffect(() => { const f = () => setReady(true); window.addEventListener("mhh-install-ready", f); return () => window.removeEventListener("mhh-install-ready", f); }, []);
  const install = async () => { const e = window.__mhhInstall; if (!e) return; e.prompt(); await e.userChoice; window.__mhhInstall = null; setReady(false); };
  return { ready, install };
}

function IosHint() {
  return <p className="text-xs muted" data-testid="ios-install-hint">On iPhone or iPad: tap the Share button, then <b>Add to Home Screen</b>. Open MyHomelyHub from your home screen to turn on alerts.</p>;
}

export function AlertsCard() {
  const { on, busy, enable, disable } = usePush();
  const { ready, install } = useInstall();
  const ok = supported();
  return (
    <section className="card p-6 space-y-3" data-testid="alerts-card">
      <h2 className="font-bold flex items-center gap-2"><Smartphone size={18} />App &amp; phone alerts</h2>
      <p className="text-sm muted">Install MyHomelyHub on your phone or computer and get instant alerts for new jobs, messages, quotes, bookings and payments, including Priority Leads.</p>
      <div className="flex flex-wrap gap-2">
        {ready && <button onClick={install} className="btn-navy btn-sm" data-testid="pwa-install-btn"><Download size={14} />Install the app</button>}
        {ok && !on && <button onClick={enable} disabled={busy} className="btn-coral btn-sm" data-testid="push-enable-btn"><BellRing size={14} />Turn on alerts on this device</button>}
        {ok && on && <><span className="chip bg-emerald-50 text-emerald-700" data-testid="push-status">Alerts on for this device</span>
          <button onClick={() => api.post("/push/test").then(() => toast.success("Test alert sent")).catch((e) => toast.error(errMsg(e)))} className="btn-ghost btn-sm" data-testid="push-test-btn">Send a test alert</button>
          <button onClick={disable} disabled={busy} className="btn-ghost btn-sm" data-testid="push-disable-btn">Turn off</button></>}
      </div>
      {!ok && (isIos() && !standalone() ? <IosHint /> : <p className="text-xs muted" data-testid="push-unsupported">This browser doesn't support app alerts. Try Chrome, Edge or Safari on a recent device.</p>)}
    </section>
  );
}

export function AlertsBanner() {
  const { user } = useAuth();
  const { on, busy, enable } = usePush();
  const [hide, setHide] = useState(() => localStorage.getItem("mhh-alerts-banner") === "hidden");
  if (!user || hide || on || !(supported() || isIos())) return null;
  const close = () => { localStorage.setItem("mhh-alerts-banner", "hidden"); setHide(true); };
  return (
    <div className="mb-4 rounded-2xl bg-[#374B5C] text-white p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="alerts-banner">
      <BellRing size={20} className="shrink-0" />
      <div className="flex-1 text-sm"><b>Get instant alerts on your phone.</b> {user.role === "provider" ? "Be first to new jobs in your area and never miss a message." : "Know the moment a pro quotes or replies."}{!supported() && isIos() && <span className="block text-xs opacity-80 mt-1">On iPhone: Share, then Add to Home Screen, then open the app to turn alerts on.</span>}</div>
      {supported() && <button onClick={enable} disabled={busy} className="btn-coral btn-sm" data-testid="alerts-banner-enable">Turn on alerts</button>}
      <button onClick={close} aria-label="Dismiss" className="opacity-80 hover:opacity-100" data-testid="alerts-banner-close"><X size={16} /></button>
    </div>
  );
}
